import { ParticleEffect } from './ParticleEffect';
import type { Particle, IntensityColorScheme } from '../types';

/**
 * Autumn leaves effect
 */
export class AutumnEffect extends ParticleEffect {
    protected getDefaultVariant(): string {
        return 'maple';
    }

    protected getDefaultColorScheme(): IntensityColorScheme {
        return {
            minimal: '#daa520',  // Goldenrod
            subtle: '#cd853f',   // Peru
            moderate: '#d2691e', // Chocolate
            bold: '#8b4513',     // Saddle brown
        };
    }

    init(width: number, height: number): void {
        const count = this.getParticleCount();
        this.particles = [];

        for (let i = 0; i < count; i++) {
            this.particles.push(this.createLeaf(width, height, true));
        }
    }

    private createLeaf(width: number, height: number, randomY = false): Particle {
        const sizeMultiplier = this.getSizeMultiplier();
        const speedMultiplier = this.getSpeedMultiplier();
        const useIntensityColor = this.customization.intensityColors || this.customization.customColor;

        const colors = ['#d2691e', '#cd853f', '#daa520', '#b8860b', '#8b4513', '#a0522d'];
        const particleColor = useIntensityColor
            ? this.getIntensityColor()
            : colors[Math.floor(Math.random() * colors.length)];

        const windStrength = this.variant === 'windy' ? 2 : 0.8;

        const particle: Particle = {
            x: Math.random() * width,
            y: randomY ? Math.random() * height : -10,
            vx: (Math.random() - 0.5) * windStrength * speedMultiplier,
            vy: (0.8 + Math.random() * 1.2) * speedMultiplier,
            size: (4 + Math.random() * 6) * sizeMultiplier,
            opacity: 0.5 + Math.random() * 0.5,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.08,
            color: particleColor,
        };

        // Try to add custom image if available
        if (this.hasCustomImages()) {
            particle.image = this.getRandomImage();
        }

        return particle;
    }

    update(_deltaTime: number, width: number, height: number): void {
        // Ensure particles have images if they loaded after particle creation
        this.ensureParticleImages();

        for (let i = 0; i < this.particles.length; i++) {
            const p = this.particles[i];
            const time = Date.now() / 1000;

            // Swaying motion
            p.x += p.vx + Math.sin(time + i) * 0.4;
            p.y += p.vy;
            p.rotation += p.rotationSpeed;

            // Reset if off screen
            if (p.y > height + 10) {
                this.particles[i] = this.createLeaf(width, height, false);
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const p of this.particles) {
            // Use custom image if available
            if (p.image && p.image.complete && p.image.naturalHeight > 0) {
                this.renderParticle(ctx, p);
            } else {
                // Default leaf rendering
                ctx.save();
                ctx.globalAlpha = p.opacity;
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);

                ctx.fillStyle = p.color!;
                ctx.beginPath();
                ctx.moveTo(0, -p.size);
                ctx.quadraticCurveTo(p.size * 0.6, -p.size * 0.5, p.size * 0.5, 0);
                ctx.quadraticCurveTo(p.size * 0.3, p.size * 0.5, 0, p.size);
                ctx.quadraticCurveTo(-p.size * 0.3, p.size * 0.5, -p.size * 0.5, 0);
                ctx.quadraticCurveTo(-p.size * 0.6, -p.size * 0.5, 0, -p.size);
                ctx.fill();

                ctx.restore();
            }
        }
    }
}
